import { useCallback, useMemo, useState } from 'react'

import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  GenericSettings,
  type ActionBarActionItem,
  type LabelAlignments,
  type SetActionBarSettings,
  type StaticField,
  FieldsSortableList,
  FieldSortableItemVariant,
} from '@tribeplatform/react-components/Views'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'

import { ActionBarSettingSections } from '../components/ActionBarSettingSections.js'
import { convertToActionItems, convertToActions } from '../utils/converters.js'
import { labelAlignmentsOptions } from '../utils/selectItems.js'
import { ActionSettings } from './ActionSettings.js'

interface Props {
  enabled: boolean
  fullWidth: boolean
  actionBar: StaticField['settings']['actionBar']
  hasMultiplePostTypes: boolean
  setFullWidth: (fullWidth: boolean) => void
  setSettings: SetActionBarSettings
  setEnabled: (enabled: boolean) => void
  goBack: () => void
}
export const ActionBarSettings = ({
  enabled,
  fullWidth,
  actionBar,
  hasMultiplePostTypes,
  setFullWidth,
  setSettings,
  setEnabled,
  goBack,
}: Props) => {
  const { $t } = useI18n()
  const [selectedActionId, setSelectedActionId] = useState<string>(null)
  const { label, labelAlignment, actions = [] } = actionBar
  const actionItems = useMemo(() => convertToActionItems(actions), [actions])

  const setAction = useCallback(
    (id: string, newAction: ActionBarActionItem) => {
      const newActionItems = actionItems.map(actionItem => {
        if (actionItem.id === id) {
          return newAction
        }

        return actionItem
      })

      setSettings('actions', convertToActions(newActionItems))
    },
    [actionItems, setSettings],
  )

  return (
    <>
      <GenericSettings
        title={
          <T defaultMessage="Action bar" id="PostsBlock.ActionBar.Title" />
        }
        description={
          <T
            defaultMessage="Action bar settings"
            id="PostsBlock.ActionBarSettings.Description"
          />
        }
        goBack={goBack}
      >
        <ActionBarSettingSections>
          <ActionBarSettingSections.General>
            <Toggle
              title={$t({ defaultMessage: 'Visible', id: 'Generics.Visible' })}
              checked={enabled}
              onChange={value => {
                setEnabled(value as boolean)
              }}
            />
            <Toggle
              title={$t({
                defaultMessage: 'Full width',
                id: 'FieldSettings.IsFullWidth',
              })}
              checked={fullWidth}
              onChange={value => {
                setFullWidth(!!value)
              }}
            />
          </ActionBarSettingSections.General>
          <ActionBarSettingSections.Label>
            <FormControl.Input
              name="label"
              label={<T id="Generics.Label.Noun" defaultMessage="Label" />}
              value={label}
              onChange={e => setSettings('label', e.target.value)}
            />
            <FormControl.ButtonGroupSelect
              name="labelAlignment"
              label={<T id="Generics.Alignment" defaultMessage="Alignment" />}
              fullWidth
              items={labelAlignmentsOptions}
              value={labelAlignment}
              onChange={(value: LabelAlignments) =>
                setSettings('labelAlignment', value)
              }
            />
          </ActionBarSettingSections.Label>
          <ActionBarSettingSections.Actions>
            <FieldsSortableList
              items={actionItems}
              onReorder={newActions =>
                setSettings('actions', convertToActions(newActions))
              }
              onToggle={id => {
                const action = actionItems.find(item => item.id === id)
                const newAction: ActionBarActionItem = {
                  ...action,
                  enabled: !action.enabled,
                }
                setAction(id, newAction)
              }}
              onClick={id => setSelectedActionId(id)}
              hasArrow
              variant={FieldSortableItemVariant.SWITCH}
            />
          </ActionBarSettingSections.Actions>
        </ActionBarSettingSections>
      </GenericSettings>
      {!!selectedActionId && (
        <ActionSettings
          action={actionItems.find(({ id }) => id === selectedActionId)}
          hasMultiplePostTypes={hasMultiplePostTypes}
          setAction={action => setAction(selectedActionId, action)}
          goBack={() => setSelectedActionId(null)}
        />
      )}
    </>
  )
}
